import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { formatRemainingDuration, getTimeSignals } from '../../useRoomPhase';

function BetContextStickyBarSP({ event, items = [], round }) {
  const [now, setNow] = useState(() => new Date());
  const timeSignals = useMemo(() => getTimeSignals(round, now), [
    now,
    round?.endtime,
    round?.fasttime,
    round?.starttime,
  ]);
  const isManualBetClose = event?.bet_close_mode === 'manual';
  const remainingSeconds = timeSignals?.remainingSeconds;
  const progressValue = timeSignals?.progressRatio === null || timeSignals?.progressRatio === undefined
    ? 0
    : Math.max(0, Math.min(100, Number(timeSignals.progressRatio) * 100));
  const timeLabel = isManualBetClose ? '' : formatRemainingDuration(remainingSeconds);
  const showTimeRow = !isManualBetClose && Boolean(timeLabel);

  useEffect(() => {
    setNow(new Date());

    if (isManualBetClose) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isManualBetClose, round?.endtime, round?.fasttime, round?.starttime]);

  if (items.length < 2 || (!isManualBetClose && !timeLabel)) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 8,
        left: 8,
        right: 8,
        zIndex: 1100,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'rgba(255,255,255,0.96)',
        border: '1px solid rgba(15, 23, 42, 0.14)',
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.18)',
        backdropFilter: 'blur(10px)',
        pointerEvents: 'none',
      }}
    >
      {showTimeRow && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(92px, 38%)',
            gap: 1,
            alignItems: 'center',
            px: 1,
            py: 0.75,
            borderBottom: '1px solid rgba(15, 23, 42, 0.10)',
          }}
        >
          <Box sx={{ fontSize: '12px', fontWeight: 900, color: 'rgba(15, 23, 42, 0.78)' }}>
            残り {timeLabel}
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 7,
              borderRadius: 999,
              bgcolor: 'rgba(15, 23, 42, 0.12)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: progressValue <= 15 ? '#d32f2f' : '#1976d2',
              },
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        }}
      >
        {items.map((item) => (
          <Box
            key={item.index}
            sx={{
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              gap: 0.5,
              alignItems: 'center',
              px: 0.85,
              py: 0.65,
              color: item.color.primary,
              borderRight: item.index % 2 === 0 ? '1px solid rgba(15, 23, 42, 0.10)' : 'none',
              borderBottom: item.index < 2 && items.length > 2 ? '1px solid rgba(15, 23, 42, 0.10)' : 'none',
              bgcolor: item.color.surface,
            }}
          >
            <Box sx={{ fontSize: '12px', fontWeight: 900 }}>{item.shortLabel}</Box>
            <Box
              sx={{
                minWidth: 0,
                fontSize: '11px',
                fontWeight: 800,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.choice}
            </Box>
          </Box>
        ))}
        {items.length === 3 && (
          <Box
            sx={{
              borderBottom: '1px solid rgba(15, 23, 42, 0.10)',
              bgcolor: 'rgba(15, 23, 42, 0.02)',
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default BetContextStickyBarSP;
